/**
 * Shared utility functions for city data handling.
 * These functions ensure consistency across the codebase for:
 * - Slug generation
 * - Display name extraction
 * - City object normalization
 * 
 * IMPORTANT: City objects must match the backend API format:
 * - short_name (required)
 * - long_name (required)
 * - identifying_name (required)
 * - slug (optional)
 * - coordinates: { latitude, longitude } (optional)
 * - google_places_data (optional)
 */

import { CitySearchResult } from '@/types/city';
import { debug } from '@/utils/logger';
import { getFirstPhotoUrl } from '@/utils/photoUtils';
import Location from '@/models/location';

/**
 * Normalized city object structure for API requests.
 * Matches the backend API format exactly.
 */
export interface CityObject {
  /** Short display name (e.g., "Bangkok") - REQUIRED */
  short_name: string;
  /** Full display name (e.g., "Bangkok, Thailand") - REQUIRED */
  long_name: string;
  /** Unique identifier (e.g., "Bangkok, Thailand") - REQUIRED */
  identifying_name: string;
  /** URL-friendly slug (e.g., "bangkok-thailand") - Optional but recommended */
  slug?: string;
  /** Geographic coordinates - Optional */
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  /** Full Google Places API response - Optional */
  google_places_data?: CitySearchResult['google_places_data'];
}

/**
 * Generates a URL-friendly slug from a city name.
 * Matches the backend's slugify logic exactly.
 * 
 * @param value - City name to slugify
 * @returns Slug identifier (e.g., "new-york", "sao-paulo", "munchen")
 * 
 * @example
 * ```typescript
 * slugifyCity("New York") // "new-york"
 * slugifyCity("São Paulo") // "sao-paulo"
 * slugifyCity("München") // "munchen"
 * ```
 */
export function slugifyCity(value: string | undefined): string {
  if (!value) return '';

  const trimmed = value.trim();
  if (!trimmed) return '';

  // Backend slugify logic: lowercase, remove special chars, replace spaces with hyphens
  return trimmed
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Extracts coordinates from city object and converts to backend format.
 * Handles multiple coordinate formats and converts to { latitude, longitude }.
 * Only includes coordinates if they are valid (not 0,0) - matches mobile app behavior.
 * 
 * @param city - City object with coordinates in any format
 * @returns Coordinates in backend format or undefined
 */
function extractCoordinates(city: CitySearchResult): { latitude: number; longitude: number } | undefined {
  // Priority 1: google_places_data.location (most reliable)
  if (city.google_places_data?.location) {
    let lat = city.google_places_data.location.latitude;
    let lng = city.google_places_data.location.longitude;

    // Handle MongoDB $numberDouble format
    if (typeof lat === 'object' && lat !== null && '$numberDouble' in lat) {
      lat = Number((lat as { $numberDouble: string }).$numberDouble);
    }
    if (typeof lng === 'object' && lng !== null && '$numberDouble' in lng) {
      lng = Number((lng as { $numberDouble: string }).$numberDouble);
    }

    if (typeof lat === 'number' && typeof lng === 'number') {
      // Validate range
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        // Only include if coordinates are valid (not 0,0) - matches mobile app
        if (lat !== 0 || lng !== 0) {
          return { latitude: lat, longitude: lng };
        }
      }
    }
  }

  // Priority 2: geo_position (legacy format)
  if (city.geo_position) {
    const lat = city.geo_position.lat;
    const lng = city.geo_position.long;
    if (typeof lat === 'number' && typeof lng === 'number') {
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        // Only include if coordinates are valid (not 0,0) - matches mobile app
        if (lat !== 0 || lng !== 0) {
          return { latitude: lat, longitude: lng };
        }
      }
    }
  }

  // Priority 3: geoposition (GeoJSON format: [longitude, latitude])
  if (city.geoposition?.coordinates?.length === 2) {
    const [lng, lat] = city.geoposition.coordinates;
    if (typeof lat === 'number' && typeof lng === 'number') {
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        // Only include if coordinates are valid (not 0,0) - matches mobile app
        if (lat !== 0 || lng !== 0) {
          return { latitude: lat, longitude: lng };
        }
      }
    }
  }

  return undefined;
}

/**
 * Extracts { lat, lng } from a city search result for map/geo center.
 * Uses same priority and validity checks as extractCoordinates.
 *
 * @param city - City search result from autocomplete API
 * @returns { lat, lng } or null if no valid coordinates
 */
export function getCoordinatesFromCitySearchResult(
  city: CitySearchResult
): { lat: number; lng: number } | null {
  const coords = extractCoordinates(city);
  if (!coords) return null;
  return { lat: coords.latitude, lng: coords.longitude };
}

/**
 * Constructs short_name and long_name from city data.
 * Matches mobile app logic: checks if city_identifying_name includes city name,
 * and prepends it if missing to ensure long_name always includes the full city name.
 * 
 * @param cityObj - City search result
 * @param fallbackName - Fallback if no name found
 * @returns Object with short_name and long_name
 */
function extractCityNames(
  cityObj: CitySearchResult,
  fallbackName?: string
): { short_name: string; long_name: string } | null {
  // Extract city name (for short_name) - prioritize label field (matches mobile app)
  const cityName =
    (typeof cityObj.label === 'string' && cityObj.label.trim()) ||
    (typeof cityObj.city === 'string' && cityObj.city.trim()) ||
    (typeof cityObj.name === 'string' && cityObj.name.trim()) ||
    fallbackName;

  if (!cityName) {
    return null;
  }

  // Get city_identifying_name (may or may not include city name)
  const cityIdentifyingName =
    (typeof cityObj.city_identifying_name === 'string' && cityObj.city_identifying_name.trim()) ||
    (typeof cityObj.identifying_name === 'string' && cityObj.identifying_name.trim()) ||
    '';

  // Construct long_name - ensure it always includes the city name
  // This matches mobile app logic exactly
  let longName: string;
  
  if (cityIdentifyingName) {
    const cityLabelLower = cityName.toLowerCase().trim();
    const identifyingNameLower = cityIdentifyingName.toLowerCase().trim();
    
    // Check if city_identifying_name already starts with the city name
    if (identifyingNameLower.startsWith(cityLabelLower)) {
      // city_identifying_name already includes the city name
      longName = cityIdentifyingName;
    } else {
      // city_identifying_name is missing the city name, prepend it
      longName = `${cityName}, ${cityIdentifyingName}`;
    }
  } else {
    // No city_identifying_name, construct from available parts
    const parts: string[] = [cityName];
    
    // Add region/state if available
    if (typeof cityObj.region === 'string' && cityObj.region.trim()) {
      const region = cityObj.region.trim();
      if (!cityName.includes(region)) {
        parts.push(region);
      }
    }

    // Add country if available
    if (typeof cityObj.country === 'string' && cityObj.country.trim()) {
      const country = cityObj.country.trim();
      if (!cityName.includes(country)) {
        parts.push(country);
      }
    }

    longName = parts.length > 1 ? parts.join(', ') : cityName;
  }

  return {
    short_name: cityName,
    long_name: longName,
  };
}

/**
 * Extracts the display name from a city object or string.
 * Tries multiple fields in order of preference, including backend format fields.
 * 
 * @param city - City object or string
 * @returns Display name or undefined if not found
 * 
 * @example
 * ```typescript
 * getCityDisplayName({ short_name: "Bangkok" }) // "Bangkok"
 * getCityDisplayName({ city: "New York" }) // "New York"
 * getCityDisplayName("Paris") // "Paris"
 * ```
 */
export function getCityDisplayName(city: unknown): string | undefined {
  if (!city) return undefined;

  // Handle string input
  if (typeof city === 'string') {
    const trimmed = city.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  // Handle object input
  if (typeof city === 'object' && city !== null) {
    const cityObj = city as Record<string, unknown>;
    
    // Try fields in order of preference (backend format first, then legacy)
    const candidates = [
      cityObj.short_name,        // Backend format
      cityObj.long_name,         // Backend format
      cityObj.city,              // Legacy format
      cityObj.name,              // Legacy format
      cityObj.label,             // Legacy format
      cityObj.city_identifying_name,
      cityObj.identifying_name,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (trimmed.length > 0) {
          return trimmed;
        }
      }
    }
  }

  return undefined;
}

/**
 * Single place label for freeform `?search=` (e.g. "Portland"), not "City, State, Country".
 * Some API payloads put the full formatted address in `short_name`; we take the first
 * comma segment from `long_name` when it looks like a multi-part address.
 */
export function getCityFreeformSearchTerm(city: unknown): string | undefined {
  if (!city || typeof city !== 'object') return undefined;
  const o = city as Record<string, unknown>;
  const shortName = typeof o.short_name === 'string' ? o.short_name.trim() : '';
  const longName = typeof o.long_name === 'string' ? o.long_name.trim() : '';

  const firstSegment = (s: string): string => {
    if (!s) return '';
    return s.includes(',') ? (s.split(',')[0]?.trim() ?? '') : s;
  };

  if (shortName && !shortName.includes(',')) return shortName;
  if (longName && longName.includes(',')) return firstSegment(longName);
  if (shortName) return firstSegment(shortName);
  if (longName) return longName;

  return undefined;
}

/**
 * Normalizes a city object to the backend API format.
 * Converts from various input formats to the exact structure expected by the backend.
 * 
 * Backend format:
 * - short_name (required)
 * - long_name (required)
 * - identifying_name (required)
 * - slug (optional)
 * - coordinates: { latitude, longitude } (optional)
 * - google_places_data (optional)
 * 
 * @param city - Raw city object from search results or profile data
 * @param fallbackName - Fallback display name if not found in city object
 * @returns Normalized city object in backend format or null if insufficient data
 * 
 * @example
 * ```typescript
 * normalizeCityObject(
 *   { city: "Bangkok", country: "Thailand", identifying_name: "Bangkok, Thailand" },
 *   "Bangkok"
 * )
 * // Returns: {
 * //   short_name: "Bangkok",
 * //   long_name: "Bangkok, Thailand",
 * //   identifying_name: "Bangkok, Thailand",
 * //   slug: "bangkok-thailand"
 * // }
 * ```
 */
export function normalizeCityObject(
  city: unknown,
  fallbackName?: string
): CityObject | null {
  if (!city) {
    // If we have a fallback name, create minimal object
    if (fallbackName) {
      const trimmed = fallbackName.trim();
      if (!trimmed) return null;
      
      const slug = slugifyCity(trimmed);
      if (!slug) return null;
      
      return {
        short_name: trimmed,
        long_name: trimmed,
        identifying_name: trimmed,
        slug,
      };
    }
    return null;
  }

  // Handle string input
  if (typeof city === 'string') {
    const trimmed = city.trim();
    if (!trimmed) return null;
    
    const slug = slugifyCity(trimmed);
    if (!slug) return null;
    
    return {
      short_name: trimmed,
      long_name: trimmed,
      identifying_name: trimmed,
      slug,
    };
  }

  // Handle object input
  if (typeof city !== 'object' || city === null) {
    return null;
  }

  const cityObj = city as CitySearchResult;

  const ownIdentifyingName =
    (typeof cityObj.identifying_name === 'string' && cityObj.identifying_name.trim()) || '';
  const cityIdentifyingName =
    (typeof cityObj.city_identifying_name === 'string' && cityObj.city_identifying_name.trim()) || '';

  // Extract short_name and long_name
  const names = extractCityNames(cityObj, fallbackName || ownIdentifyingName || cityIdentifyingName);
  if (!names) {
    return null;
  }

  // Extract identifying_name (required). City search results have no identifying_name and
  // their city_identifying_name is only "State, Country", so use long_name, which prepends
  // the city (matches createCityFromSearchResult). The backend stores city.identifying_name
  // as the user's home city, so "Florida, United States" alone would drop "Miami".
  const identifyingName =
    ownIdentifyingName || (cityIdentifyingName ? names.long_name : fallbackName);

  if (!identifyingName) {
    return null;
  }

  // Generate slug from identifying_name
  const slug = slugifyCity(identifyingName);

  // Build normalized object in backend format
  const normalized: CityObject = {
    short_name: names.short_name,
    long_name: names.long_name,
    identifying_name: identifyingName,
    slug, // Optional but recommended
  };

  // Extract and convert coordinates
  const coordinates = extractCoordinates(cityObj);
  if (coordinates) {
    normalized.coordinates = coordinates;
  }

  // Include google_places_data if available
  if (cityObj.google_places_data) {
    normalized.google_places_data = cityObj.google_places_data;
  }

  // NOTE: We explicitly do NOT include:
  // - _id (backend deletes it anyway)
  // - city_identifying_name (only stored on user root, not in city object)
  // - label, name, city (replaced by short_name/long_name)
  // - geo_position, geoposition (converted to coordinates)
  // - google_place_id (not in backend spec)
  // - photos, types, place_types (not in backend spec)

  return normalized;
}

/**
 * Creates a city object from search result, matching mobile app logic exactly.
 * Handles the case where city_identifying_name may or may not include the city name.
 * 
 * This function ensures consistent city object construction across all frontends.
 * 
 * @param selectedItem - City search result from API
 * @returns City object in backend format or null if insufficient data
 * 
 * @example
 * ```typescript
 * // Scenario 1: city_identifying_name includes city name
 * createCityFromSearchResult({
 *   label: "Las Vegas",
 *   city_identifying_name: "Las Vegas, Nevada, United States"
 * })
 * // Returns: { short_name: "Las Vegas", long_name: "Las Vegas, Nevada, United States", ... }
 * 
 * // Scenario 2: city_identifying_name missing city name
 * createCityFromSearchResult({
 *   label: "Las Vegas",
 *   city_identifying_name: "Nevada, United States"
 * })
 * // Returns: { short_name: "Las Vegas", long_name: "Las Vegas, Nevada, United States", ... }
 * ```
 */
export function createCityFromSearchResult(
  selectedItem: CitySearchResult
): CityObject | null {
  debug("[createCityFromSearchResult] Starting with search result:", {
    label: selectedItem.label,
    city: selectedItem.city,
    name: selectedItem.name,
    city_identifying_name: selectedItem.city_identifying_name,
    identifying_name: selectedItem.identifying_name,
    region: selectedItem.region,
    country: selectedItem.country,
    has_google_places_data: !!selectedItem.google_places_data,
    has_geoposition: !!selectedItem.geoposition,
    has_geo_position: !!selectedItem.geo_position,
  });

  // Extract city label (city name) - matches mobile app
  const cityLabel =
    (typeof selectedItem.label === 'string' && selectedItem.label.trim()) ||
    (typeof selectedItem.city === 'string' && selectedItem.city.trim()) ||
    (typeof selectedItem.name === 'string' && selectedItem.name.trim()) ||
    '';

  if (!cityLabel) {
    debug("[createCityFromSearchResult] No city label found, returning null");
    return null;
  }

  // Extract city_identifying_name (may or may not include city name)
  const cityIdentifyingName =
    (typeof selectedItem.city_identifying_name === 'string' && selectedItem.city_identifying_name.trim()) ||
    (typeof selectedItem.identifying_name === 'string' && selectedItem.identifying_name.trim()) ||
    '';

  debug("[createCityFromSearchResult] Extracted values:", {
    cityLabel,
    cityIdentifyingName,
  });

  // Construct long_name (for display) - ensure it includes the city name
  // This logic matches mobile app
  let longName: string;
  if (cityIdentifyingName) {
    const cityLabelLower = cityLabel.toLowerCase().trim();
    const identifyingNameLower = cityIdentifyingName.toLowerCase().trim();

    debug("[createCityFromSearchResult] Checking if city_identifying_name includes city name:", {
      cityLabelLower,
      identifyingNameLower,
      startsWith: identifyingNameLower.startsWith(cityLabelLower),
    });

    // Check if city_identifying_name already starts with the city name
    // Use a more flexible check: check if identifying_name contains the city name at the start
    // This handles cases like "Sacramento" vs "Sacramento, California, United States"
    if (identifyingNameLower.startsWith(cityLabelLower)) {
      // city_identifying_name already includes the city name
      longName = cityIdentifyingName;
      debug("[createCityFromSearchResult] city_identifying_name already includes city name, using it as long_name:", longName);
    } else {
      // city_identifying_name is missing the city name, prepend it
      // Example: "Sacramento" + ", " + "California, United States" = "Sacramento, California, United States"
      longName = `${cityLabel}, ${cityIdentifyingName}`;
      debug("[createCityFromSearchResult] Prepending city name to create long_name:", longName);
    }
  } else {
    // Fallback: try to construct from region and country if available
    const parts: string[] = [cityLabel];
    if (selectedItem.region && typeof selectedItem.region === 'string' && selectedItem.region.trim()) {
      parts.push(selectedItem.region.trim());
    }
    if (selectedItem.country && typeof selectedItem.country === 'string' && selectedItem.country.trim()) {
      parts.push(selectedItem.country.trim());
    }
    longName = parts.length > 1 ? parts.join(', ') : cityLabel;
    debug("[createCityFromSearchResult] Constructed long_name from parts:", { parts, longName });
  }

  // Construct identifying_name (for backend matching) - use long_name to ensure it includes city name
  // identifying_name should be the full location string, not just the state/country
  const identifyingName = longName || cityIdentifyingName || cityLabel;
  
  debug("[createCityFromSearchResult] Final identifying_name (using long_name):", identifyingName);

  // Handle coordinates - prioritize google_places_data.location (most accurate)
  // Then check geoposition, then other formats
  let coordinates: { latitude: number; longitude: number } | undefined;
  
  debug("[createCityFromSearchResult] Extracting coordinates...");
  
  // Priority 1: google_places_data.location (most accurate, city-level coordinates)
  if (selectedItem.google_places_data?.location) {
    debug("[createCityFromSearchResult] Found google_places_data.location:", selectedItem.google_places_data.location);
    const extracted = extractCoordinates(selectedItem);
    if (extracted) {
      coordinates = extracted;
      debug("[createCityFromSearchResult] Extracted coordinates from google_places_data.location:", coordinates);
    }
  }

  // Priority 2: geoposition (MongoDB format is [lng, lat])
  if (!coordinates && selectedItem.geoposition?.coordinates && selectedItem.geoposition.coordinates.length >= 2) {
    const lng = selectedItem.geoposition.coordinates[0];
    const lat = selectedItem.geoposition.coordinates[1];
    debug("[createCityFromSearchResult] Found geoposition.coordinates:", { lng, lat });

    // Only include if coordinates are valid (not 0,0) - matches mobile app
    if (typeof lat === 'number' && typeof lng === 'number') {
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        if (lat !== 0 || lng !== 0) {
          coordinates = { latitude: lat, longitude: lng };
          debug("[createCityFromSearchResult] Using coordinates from geoposition:", coordinates);
        } else {
          debug("[createCityFromSearchResult] Coordinates are (0,0), skipping");
        }
      } else {
        debug("[createCityFromSearchResult] Coordinates out of valid range");
      }
    }
  }

  // Priority 3: Other coordinate formats (geo_position, etc.)
  if (!coordinates) {
    debug("[createCityFromSearchResult] Trying extractCoordinates for other formats...");
    const extracted = extractCoordinates(selectedItem);
    if (extracted) {
      coordinates = extracted;
      debug("[createCityFromSearchResult] Extracted coordinates from other formats:", coordinates);
    } else {
      debug("[createCityFromSearchResult] No coordinates found in any format");
    }
  }

  // Generate slug from identifying_name
  const slug = slugifyCity(identifyingName);

  if (!slug) {
    debug("[createCityFromSearchResult] Failed to generate slug, returning null");
    return null;
  }

  const result: CityObject = {
    short_name: cityLabel,
    long_name: longName,
    identifying_name: identifyingName,
    slug,
    coordinates,
    google_places_data: selectedItem.google_places_data,
  };

  debug("[createCityFromSearchResult] Final city object:", {
    short_name: result.short_name,
    long_name: result.long_name,
    identifying_name: result.identifying_name,
    slug: result.slug,
    has_coordinates: !!result.coordinates,
    coordinates: result.coordinates,
  });

  return result;
}

/**
 * Extracts photo URL from city object.
 * Handles various photo field formats.
 * 
 * @param city - City object that may contain photos
 * @returns Photo URL or null if not found
 */
export function extractCityPhoto(city: CitySearchResult | null | undefined): string | null {
  if (!city) return null;

  // Use unified photo utility to extract from identifier format
  // Backend sends identifiers only (name, photo_reference)
  const photoUrl = getFirstPhotoUrl(
    {
      photos: Array.isArray(city.photos) ? city.photos : undefined,
    },
    400
  );

  return photoUrl || null;
}

/**
 * Gets the display text for city:
 * 1. subtitle (custom user subtitle) - if available
 * 2. a3_intl_address (full format)
 * 3. long_name (full format)
 * 4. city_identifying_name (may be incomplete - will be completed if city name available)
 * 5. identifying_name (legacy)
 * 
 * Handles incomplete city_identifying_name by prepending city name if available.
 * 
 * @param city - City object from profile
 * @param subtitle - Optional custom user subtitle
 * @returns Display text or undefined
 * 
 * @example
 * ```typescript
 * getCityDisplayText({ city: "Atlanta", city_identifying_name: "Georgia, United States" })
 * // Returns: "Atlanta, Georgia, United States"
 * 
 * getCityDisplayText({ city_identifying_name: "Atlanta, Georgia, United States" })
 * // Returns: "Atlanta, Georgia, United States" (already complete)
 * ```
 */
/**
 * For labels like "City, State/Region, Country" (three or more comma-separated
 * segments), returns "City, Country" for compact UI. Two or fewer segments
 * are returned unchanged (e.g. "Paris, France", "Japan", "Tuscany, Italy").
 */
export function formatLocationLabelCityCountry(label: string | undefined | null): string {
  if (label == null || typeof label !== 'string') return '';
  const trimmed = label.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 3) {
    return `${parts[0]}, ${parts[parts.length - 1]}`;
  }
  return trimmed;
}

export function getCityDisplayText(
  city: unknown,
  subtitle?: string
): string | undefined {
  // Priority 1: User's custom subtitle (if provided)
  if (subtitle && typeof subtitle === 'string' && subtitle.trim()) {
    return subtitle.trim();
  }

  if (!city || typeof city !== 'object' || city === null) {
    return undefined;
  }

  const cityObj = city as Record<string, unknown>;

  // Helper function to extract city/state/country from various address formats
  const extractCityStateCountry = (): string | null => {
    // Try city_identifying_name first (most complete)
    if (typeof cityObj.city_identifying_name === 'string' && cityObj.city_identifying_name.trim()) {
      const identifyingName = cityObj.city_identifying_name.trim();
      const parts = identifyingName.split(',').map(p => p.trim());
      
      // Check if first part is a street address
      const firstPart = parts[0];
      const isFirstPartStreet = /^\d+/.test(firstPart) || 
                              /\b(Ave|Avenue|St|Street|Rd|Road|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pl|Place|Way|Cir|Circle)\b/i.test(firstPart);
      
      if (isFirstPartStreet && parts.length >= 3) {
        // Skip street address, extract city, state, country
        // Handle repeated city: "Street, City, City, State, Country"
        let cityIndex = 1;
        if (parts.length > 3 && parts[1] === parts[2]) {
          cityIndex = 2;
        }
        
        if (parts.length >= cityIndex + 2) {
          return parts.slice(cityIndex).join(', ');
        }
      } else if (!isFirstPartStreet && parts.length >= 2) {
        // Already in city/state/country format, use it
        return identifyingName;
      }
    }
    
    // Try a3_intl_address
    if (typeof cityObj.a3_intl_address === 'string' && cityObj.a3_intl_address.trim()) {
      const a3Address = cityObj.a3_intl_address.trim();
      const a3Parts = a3Address.split(',').map(p => p.trim());
      
      if (a3Parts.length >= 3) {
        const firstPart = a3Parts[0];
        const isFirstPartStreet = /^\d+/.test(firstPart) || 
                                /\b(Ave|Avenue|St|Street|Rd|Road|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pl|Place|Way|Cir|Circle)\b/i.test(firstPart);
        
        if (isFirstPartStreet) {
          // Extract city, state (remove zip), country
          const city = a3Parts[1];
          const statePart = a3Parts[2];
          const state = statePart.replace(/\s+\d{5}(-\d{4})?$/, '');
          const country = a3Parts[a3Parts.length - 1];
          return `${city}, ${state}, ${country}`;
        } else if (a3Parts.length >= 2) {
          // Already in city/state/country format, but might have zip - clean it
          const cleaned = a3Parts.map((part, idx) => {
            // Remove zip code from state part (usually index 1 or 2)
            if (idx > 0 && idx < a3Parts.length - 1) {
              return part.replace(/\s+\d{5}(-\d{4})?$/, '');
            }
            return part;
          }).join(', ');
          return cleaned;
        }
      } else if (a3Parts.length === 2) {
        // City, State format - might need country
        return a3Address;
      }
    }
    
    // Try long_name
    if (typeof cityObj.long_name === 'string' && cityObj.long_name.trim()) {
      const longName = cityObj.long_name.trim();
      const parts = longName.split(',').map(p => p.trim());
      
      // Check if it's a street address
      const firstPart = parts[0];
      const isFirstPartStreet = /^\d+/.test(firstPart) || 
                              /\b(Ave|Avenue|St|Street|Rd|Road|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pl|Place|Way|Cir|Circle)\b/i.test(firstPart);
      
      if (!isFirstPartStreet && parts.length >= 2) {
        // Clean zip codes if present
        const cleaned = parts.map((part, idx) => {
          if (idx > 0 && idx < parts.length - 1) {
            return part.replace(/\s+\d{5}(-\d{4})?$/, '');
          }
          return part;
        }).join(', ');
        return cleaned;
      }
    }
    
    return null;
  };

  // Priority 2: shortFormattedAddress from google_places_data
  // Always try to construct "City, State, Country" format
  if (cityObj.google_places_data && typeof cityObj.google_places_data === 'object') {
    const googlePlacesData = cityObj.google_places_data as Record<string, unknown>;
    
    // Check if shortFormattedAddress field exists
    const shortFormatted = typeof googlePlacesData.shortFormattedAddress === 'string'
      ? googlePlacesData.shortFormattedAddress
      : typeof googlePlacesData.short_formatted_address === 'string'
      ? googlePlacesData.short_formatted_address
      : '';
    
    if (shortFormatted && shortFormatted.trim()) {
      const trimmedShort = shortFormatted.trim();
      
      // Check if this is already in "City, State, Country" format (has at least 2 commas or state abbreviation pattern)
      const hasStateCountry = /,\s*[A-Z]{2}\s*,/.test(trimmedShort) || // US format: ", CA, "
                             (trimmedShort.split(',').length >= 3); // Has 3+ parts
      
      // Check if this is a street address
      const isStreetAddress = /^\d+/.test(trimmedShort) || 
                             /\b(Ave|Avenue|St|Street|Rd|Road|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pl|Place|Way|Cir|Circle)\b/i.test(trimmedShort);
      
      if (hasStateCountry && !isStreetAddress) {
        // Already in proper format, use it
        return trimmedShort;
      } else {
        // Not in full format - try to extract/construct city/state/country from other fields
        const extracted = extractCityStateCountry();
        if (extracted) {
          return extracted;
        }
        
        // If extraction failed but we have a city name, try to append state/country
        if (!isStreetAddress && trimmedShort.split(',').length === 1) {
          // Just a city name - try to get state/country from other fields
          if (typeof cityObj.city_identifying_name === 'string' && cityObj.city_identifying_name.trim()) {
            const identifyingName = cityObj.city_identifying_name.trim();
            const parts = identifyingName.split(',').map(p => p.trim());
            
            // Find the city in identifying_name and get what comes after
            const cityIndex = parts.findIndex(p => p.toLowerCase() === trimmedShort.toLowerCase());
            if (cityIndex >= 0 && parts.length > cityIndex + 1) {
              const stateCountry = parts.slice(cityIndex + 1).join(', ');
              return `${trimmedShort}, ${stateCountry}`;
            }
          }
        }
        
        // Last resort: use shortFormatted as-is
        return trimmedShort;
      }
    } else {
      // Try to extract from other fields even without shortFormattedAddress
      const extracted = extractCityStateCountry();
      if (extracted) {
        return extracted;
      }
    }
  } else {
    // Try to extract from other fields
    const extracted = extractCityStateCountry();
    if (extracted) {
      return extracted;
    }
  }

  // Priority 3: a3_intl_address (may contain full addresses with zip codes or street addresses)
  // Try to extract city/state/country format, removing street addresses and zip codes
  if (typeof cityObj.a3_intl_address === 'string' && cityObj.a3_intl_address.trim()) {
    const address = cityObj.a3_intl_address.trim();
    const parts = address.split(',').map(p => p.trim());
    
    const isStreetAddress = /^\d+/.test(parts[0]) || 
                          /\b(Ave|Avenue|St|Street|Rd|Road|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pl|Place|Way|Cir|Circle)\b/i.test(parts[0]);
    const hasZipCode = /\d{5}/.test(address);
    
    if (isStreetAddress && parts.length >= 3) {
      // Extract city, state (remove zip), country
      const city = parts[1];
      const statePart = parts[2];
      const state = statePart.replace(/\s+\d{5}(-\d{4})?$/, '');
      const country = parts[parts.length - 1];
      return `${city}, ${state}, ${country}`;
    } else if (hasZipCode && parts.length >= 2) {
      // Remove zip codes from state part
      const cleaned = parts.map((part, idx) => {
        if (idx > 0 && idx < parts.length - 1) {
          return part.replace(/\s+\d{5}(-\d{4})?$/, '');
        }
        return part;
      }).join(', ');
      return cleaned;
    } else {
      // Already in good format or can't improve it
      return address;
    }
  }

  // Priority 4: long_name (if available)
  if (typeof cityObj.long_name === 'string' && cityObj.long_name.trim()) {
    return cityObj.long_name.trim();
  }

  // Priority 5: city_identifying_name (may be incomplete)
  if (typeof cityObj.city_identifying_name === 'string' && cityObj.city_identifying_name.trim()) {
    const identifyingName = cityObj.city_identifying_name.trim();
    
    // If we have city name separately, ensure it's included
    if (typeof cityObj.city === 'string' && cityObj.city.trim()) {
      const cityName = cityObj.city.trim();
      const identifyingNameLower = identifyingName.toLowerCase();
      const cityNameLower = cityName.toLowerCase();
      
      // If identifying_name doesn't start with city name, prepend it
      if (!identifyingNameLower.startsWith(cityNameLower)) {
        return `${cityName}, ${identifyingName}`;
      }
    }
    
    return identifyingName;
  }

  // Priority 6: identifying_name (legacy)
  if (typeof cityObj.identifying_name === 'string' && cityObj.identifying_name.trim()) {
    const identifyingName = cityObj.identifying_name.trim();
    
    // If we have city name separately, ensure it's included
    if (typeof cityObj.city === 'string' && cityObj.city.trim()) {
      const cityName = cityObj.city.trim();
      const identifyingNameLower = identifyingName.toLowerCase();
      const cityNameLower = cityName.toLowerCase();
      
      // If identifying_name doesn't start with city name, prepend it
      if (!identifyingNameLower.startsWith(cityNameLower)) {
        return `${cityName}, ${identifyingName}`;
      }
    }
    
    return identifyingName;
  }

  // Priority 7: city name only (fallback)
  if (typeof cityObj.city === 'string' && cityObj.city.trim()) {
    return cityObj.city.trim();
  }

  return undefined;
}

/**
 * Default text for list inline place search: matches the list hero destination chip
 * (compact city/country via formatLocationLabelCityCountry + getCityDisplayText).
 * When primary_destination is unset, falls back to legacy wizard `city` so create/edit
 * still gets suggestions before backend-only flows land.
 */
export function getListInlineSearchSeedDestination(formData: {
  primary_destination?: unknown;
  city?: unknown;
} | null | undefined): string {
  if (!formData) return '';
  const pd = formData.primary_destination;
  if (pd) {
    const t = formatLocationLabelCityCountry(getCityDisplayText(pd) ?? '').trim();
    if (t) return t;
  }
  const c = formData.city;
  if (c) {
    return formatLocationLabelCityCountry(getCityDisplayText(c) ?? '').trim();
  }
  return '';
}

/**
 * Checks if an address string is in short format (contains state abbreviation like ", NY, " or ", CA, ").
 * 
 * @param address - Address string to check
 * @returns True if address matches short format pattern
 */
function isShortFormatAddress(address: string): boolean {
  return /,\s*[A-Z]{2}\s*,/.test(address);
}

/**
 * Extracts short format address from Google Places data.
 * Priority: shortFormattedAddress > formattedAddress (if short format) > fallback
 * 
 * @param googlePlacesData - Google Places data object (can be Record<string, unknown>)
 * @param fallback - Fallback address if no short format found
 * @returns Short format address or fallback
 */
export function extractShortFormatAddress(
  googlePlacesData: Record<string, unknown> | null | undefined,
  fallback: string = ''
): string {
  if (!googlePlacesData || typeof googlePlacesData !== 'object') {
    return fallback;
  }

  // Try shortFormattedAddress (camelCase or snake_case)
  const shortFormatted = typeof googlePlacesData.shortFormattedAddress === 'string'
    ? googlePlacesData.shortFormattedAddress
    : typeof googlePlacesData.short_formatted_address === 'string'
    ? googlePlacesData.short_formatted_address
    : '';

  if (shortFormatted && isShortFormatAddress(shortFormatted)) {
    return shortFormatted;
  }

  // Try formattedAddress (camelCase or snake_case) - check if it's short format
  const formatted = typeof googlePlacesData.formattedAddress === 'string'
    ? googlePlacesData.formattedAddress
    : typeof googlePlacesData.formatted_address === 'string'
    ? googlePlacesData.formatted_address
    : '';

  if (formatted && isShortFormatAddress(formatted)) {
    return formatted;
  }

  // Fallback: return formatted address even if not short format, or use provided fallback
  return formatted || fallback;
}

/**
 * Gets a short address string from a Location object.
 * Uses getCityDisplayText to format the address consistently.
 * 
 * @param locationData - Location object with address information
 * @returns Formatted address string or undefined
 */
export function getShortAddress(locationData: Location | null | undefined): string | undefined {
  if (!locationData) return undefined;

  const cityLikeData: Record<string, unknown> = {
    city: locationData.name,
    a3_intl_address: locationData.a3_intl_address || (typeof locationData.address === 'string' ? locationData.address : undefined),
    long_name: locationData.name,
    google_places_data: locationData.google_places_data,
  };

  const displayText = getCityDisplayText(cityLikeData);
  
  return displayText || undefined;
}

/**
 * City / country line for list inline add-place cards — same compact rules as the list hero
 * (getCityDisplayText + formatLocationLabelCityCountry). Omits using the venue name as
 * `city` so subtitles are locality-focused, not a duplicate of the place title.
 */
export function getListInlineSearchResultLocationLabel(
  locationData: Location | null | undefined
): string {
  if (!locationData) return '';

  const addr =
    locationData.a3_intl_address ||
    (typeof locationData.address === 'string' ? locationData.address : undefined);

  const cityLikeData: Record<string, unknown> = {
    a3_intl_address: addr,
    google_places_data: locationData.google_places_data,
    city_identifying_name: locationData.city_identifying_name,
    country: locationData.country?.trim() ? locationData.country.trim() : undefined,
  };

  const line = getCityDisplayText(cityLikeData);
  return formatLocationLabelCityCountry(line ?? '').trim();
}
